// src/pages/admin/AdminRegistrations.jsx
//
// Founder-only registration contact fixer at /admin/registrations.
// Replaces the Admin-SDK-shell flow Kurt was running to patch
// parentPhone / parentEmail / parentName / language on registrations.
// Mirrors the AdminClubsList + AdminClubDetail patterns: search,
// edit form, confirm modal, audit history strip.
//
// Single-file page (search + detail in one route via state) because
// the spec is small enough that splitting into a list/detail pair
// would be more code than it's worth. See
// features/admin-registrations/01-spec.md.

import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  getDoc,
  getDocs,
  limit,
  orderBy,
  query,
  where,
} from 'firebase/firestore';
import { httpsCallable, getFunctions } from 'firebase/functions';
import toast from 'react-hot-toast';
import { db } from '@/firebase/firebase';
import { usePlatformClaims } from '@/hooks/usePlatformClaims';
import {
  ArrowLeftIcon,
  ArrowPathIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  MagnifyingGlassIcon,
} from '@heroicons/react/24/outline';
import ConfirmTierChangeModal from '@/components/admin/ConfirmTierChangeModal';

const HEAD_FONT = "'Barlow Condensed', sans-serif";
const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// ─── Phone normalizer (mirrors functions/core/phone.js) ───────────────
function toE164US(input) {
  if (input == null) return null;
  const raw = String(input).trim();
  if (!raw) return null;
  if (/^\+1\d{10}$/.test(raw)) return raw;
  const digits = raw.replace(/\D/g, '');
  if (!digits) return null;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  if (digits.length === 10) return `+1${digits}`;
  return null;
}

function isValidUsE164(v) {
  return typeof v === 'string' && /^\+1\d{10}$/.test(v);
}

// ─── Hash function — MUST match server hashVersion ────────────────────
async function hashVersion(obj) {
  const norm = {
    parentName:   obj.parentName == null ? null : String(obj.parentName),
    parentPhone:  obj.parentPhone == null ? null : toE164US(obj.parentPhone),
    parentEmail:  obj.parentEmail == null ? null : String(obj.parentEmail).toLowerCase(),
    language:     obj.language == null ? null : String(obj.language),
  };
  const buf = new TextEncoder().encode(JSON.stringify(norm));
  const digest = await crypto.subtle.digest('SHA-256', buf);
  return Array.from(new Uint8Array(digest))
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('');
}

function formatAuditTimestamp(ts) {
  if (!ts) return '';
  const d = typeof ts.toDate === 'function' ? ts.toDate() : new Date(ts);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleString(undefined, {
    month:  '2-digit',
    day:    '2-digit',
    hour:   '2-digit',
    minute: '2-digit',
  });
}

export default function AdminRegistrations() {
  const { isFounder } = usePlatformClaims();
  const blocked = !isFounder;

  // Search state
  const [searchInput, setSearchInput] = useState('');
  const [searching, setSearching]     = useState(false);
  const [searchError, setSearchError] = useState(null);
  const [results, setResults]         = useState(null); // null = no search yet

  // Detail state
  const [selectedId, setSelectedId] = useState(null);
  const [reg, setReg]               = useState(null);
  const [audits, setAudits]         = useState([]);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [detailError, setDetailError] = useState(null);

  // Edit form state
  const [parentName, setParentName]   = useState('');
  const [parentPhone, setParentPhone] = useState('');
  const [parentEmail, setParentEmail] = useState('');
  const [language, setLanguage]       = useState('');

  // Confirm modal
  const [confirmOpen, setConfirmOpen]         = useState(false);
  const [confirmInFlight, setConfirmInFlight] = useState(false);
  const [confirmError, setConfirmError]       = useState(null);

  async function runSearch() {
    const needle = searchInput.trim();
    if (!needle) return;
    setSearching(true);
    setSearchError(null);
    setResults(null);
    try {
      const tasks = [];

      // Try as registrationId
      tasks.push(
        (async () => {
          try {
            const snap = await getDoc(doc(db, 'registrations', needle));
            return snap.exists() ? [{ id: snap.id, ...snap.data(), _match: 'id' }] : [];
          } catch { return []; }
        })(),
      );

      // Try as phone
      const phoneE164 = toE164US(needle);
      if (phoneE164) {
        tasks.push(
          (async () => {
            try {
              const q = query(
                collection(db, 'registrations'),
                where('parentPhone', '==', phoneE164),
                limit(25),
              );
              const snap = await getDocs(q);
              return snap.docs.map((d) => ({ id: d.id, ...d.data(), _match: 'phone' }));
            } catch { return []; }
          })(),
        );
      }

      // Try as email
      if (EMAIL_RE.test(needle)) {
        tasks.push(
          (async () => {
            try {
              const q = query(
                collection(db, 'registrations'),
                where('parentEmailLowercased', '==', needle.toLowerCase()),
                limit(25),
              );
              const snap = await getDocs(q);
              return snap.docs.map((d) => ({ id: d.id, ...d.data(), _match: 'email' }));
            } catch {
              // Fallback: some legacy docs may only have parentEmail (no lowercased mirror)
              try {
                const q2 = query(
                  collection(db, 'registrations'),
                  where('parentEmail', '==', needle),
                  limit(25),
                );
                const snap = await getDocs(q2);
                return snap.docs.map((d) => ({ id: d.id, ...d.data(), _match: 'email' }));
              } catch { return []; }
            }
          })(),
        );
      }

      const arrays = await Promise.all(tasks);
      const merged = new Map();
      for (const arr of arrays) {
        for (const r of arr) if (!merged.has(r.id)) merged.set(r.id, r);
      }
      setResults([...merged.values()]);
    } catch (e) {
      setSearchError(e?.message || 'Search failed.');
    } finally {
      setSearching(false);
    }
  }

  async function openDetail(registrationId) {
    setSelectedId(registrationId);
    setLoadingDetail(true);
    setDetailError(null);
    setReg(null);
    setAudits([]);
    try {
      const regSnap = await getDoc(doc(db, 'registrations', registrationId));
      if (!regSnap.exists()) {
        setDetailError('not-found');
        setLoadingDetail(false);
        return;
      }
      const r = { id: regSnap.id, ...regSnap.data() };
      setReg(r);
      setParentName(r.parentName || '');
      setParentPhone(r.parentPhone || '');
      setParentEmail(r.parentEmail || '');
      setLanguage(r.language || '');

      // Audit history
      try {
        const aq = query(
          collection(db, 'adminAudit'),
          where('targetType', '==', 'registration'),
          where('targetId',   '==', registrationId),
          orderBy('createdAt', 'desc'),
          limit(10),
        );
        const aSnap = await getDocs(aq);
        setAudits(aSnap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch { /* ignore audit-history load failure */ }
    } catch (e) {
      setDetailError(e?.message || 'Failed to load registration.');
    } finally {
      setLoadingDetail(false);
    }
  }

  function closeDetail() {
    setSelectedId(null);
    setReg(null);
    setAudits([]);
    setDetailError(null);
  }

  // ── Delta computation ───────────────────────────────────────────────
  const deltas = useMemo(() => {
    if (!reg) return [];
    const proposed = {
      parentName:  parentName.trim() === '' ? null : parentName.trim(),
      parentPhone: parentPhone.trim() === '' ? null : toE164US(parentPhone),
      parentEmail: parentEmail.trim() === '' ? null : parentEmail.trim(),
      language:    language === '' ? null : language,
    };
    const before = {
      parentName:  reg.parentName ?? null,
      parentPhone: reg.parentPhone ?? null,
      parentEmail: reg.parentEmail ?? null,
      language:    reg.language ?? null,
    };
    const out = [];
    for (const f of ['parentName', 'parentPhone', 'parentEmail', 'language']) {
      if (proposed[f] !== before[f]) {
        out.push({ field: f, before: before[f], after: proposed[f] });
      }
    }
    return out;
  }, [reg, parentName, parentPhone, parentEmail, language]);

  const dirty = deltas.length > 0;

  // Client-side validation (server re-validates).
  const validationError = useMemo(() => {
    if (parentPhone.trim() !== '') {
      const n = toE164US(parentPhone);
      if (!n || !isValidUsE164(n)) {
        return 'parentPhone must be a valid 10-digit US number or blank.';
      }
    }
    if (parentEmail.trim() !== '' && !EMAIL_RE.test(parentEmail.trim())) {
      return 'parentEmail is malformed.';
    }
    if (parentName.trim().length > 200) {
      return 'parentName must be 200 chars or fewer.';
    }
    return null;
  }, [parentName, parentPhone, parentEmail]);

  function openConfirm() {
    if (validationError) {
      toast.error(validationError);
      return;
    }
    setConfirmError(null);
    setConfirmOpen(true);
  }

  async function handleConfirmSave(reason) {
    if (!reg) return;
    setConfirmInFlight(true);
    setConfirmError(null);
    try {
      const loadedSnapshot = {
        parentName:  reg.parentName ?? null,
        parentPhone: reg.parentPhone ?? null,
        parentEmail: reg.parentEmail ?? null,
        language:    reg.language ?? null,
      };
      const expectedVersion = await hashVersion(loadedSnapshot);

      const payload = {
        registrationId: reg.id,
        reason,
        expectedVersion,
      };
      for (const { field, after } of deltas) {
        payload[field] = after;
      }

      const fn = httpsCallable(getFunctions(undefined, 'us-central1'), 'setRegistrationContact');
      await fn(payload);

      toast.success('Saved.');
      setConfirmOpen(false);
      // Refetch detail
      openDetail(reg.id);
    } catch (e) {
      const msg = e?.message || e?.details || 'Save failed. Try again.';
      setConfirmError(msg);
    } finally {
      setConfirmInFlight(false);
    }
  }

  if (blocked) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="rounded-2xl border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 flex items-start gap-2">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 mt-0.5" />
          <p>
            <span className="font-semibold">Insufficient privilege.</span>{' '}
            Founder claim required to view registration provisioning.
          </p>
        </div>
      </div>
    );
  }

  // ── Detail view (overlay on top of search via state) ────────────────
  if (selectedId && reg) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button
          type="button"
          onClick={closeDetail}
          className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4"
        >
          <ArrowLeftIcon className="h-4 w-4" />
          Back to search
        </button>

        <h1
          className="text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-1"
          style={{ fontFamily: HEAD_FONT }}
        >
          {reg.parentName || <span className="text-zinc-400 italic">(no parent name)</span>}
        </h1>
        <p className="text-xs text-zinc-500 mb-4">
          ID: <span className="font-mono">{reg.id}</span>
        </p>

        {/* Read-only summary */}
        <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-4 mb-4 text-sm grid grid-cols-1 sm:grid-cols-2 gap-2 text-zinc-700">
          {reg.teamId && (
            <div><span className="text-zinc-500">Team:</span> <span className="font-mono text-xs">{reg.teamId}</span></div>
          )}
          {reg.seasonId && (
            <div><span className="text-zinc-500">Season:</span> <span className="font-mono text-xs">{reg.seasonId}</span></div>
          )}
          {reg.status && (
            <div><span className="text-zinc-500">Status:</span> {reg.status}</div>
          )}
          {reg.createdAt && (
            <div><span className="text-zinc-500">Created:</span> {formatAuditTimestamp(reg.createdAt)}</div>
          )}
          {reg.lastContactUpdateAt && (
            <div className="sm:col-span-2">
              <span className="text-zinc-500">Last contact edit:</span> {formatAuditTimestamp(reg.lastContactUpdateAt)}
            </div>
          )}
        </div>

        {/* Edit form */}
        <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5 mb-4">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-4">
            Contact fields
          </h2>

          <div className="space-y-4">
            <div>
              <label htmlFor="field-name" className="block text-sm font-medium text-zinc-700">
                Parent name
              </label>
              <input
                id="field-name"
                type="text"
                value={parentName}
                maxLength={200}
                onChange={(e) => setParentName(e.target.value)}
                placeholder="(blank)"
                className="mt-1 block w-full sm:w-96 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div>
              <label htmlFor="field-phone" className="block text-sm font-medium text-zinc-700">
                Parent phone
              </label>
              <input
                id="field-phone"
                type="tel"
                value={parentPhone}
                onChange={(e) => setParentPhone(e.target.value)}
                placeholder="+1XXXXXXXXXX or (XXX) XXX-XXXX"
                className="mt-1 block w-full sm:w-72 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
              {parentPhone.trim() !== '' && (
                <p className="mt-1 text-xs text-zinc-500">
                  Will be saved as: <span className="font-mono">{toE164US(parentPhone) || '(invalid)'}</span>
                </p>
              )}
            </div>

            <div>
              <label htmlFor="field-email" className="block text-sm font-medium text-zinc-700">
                Parent email
              </label>
              <input
                id="field-email"
                type="email"
                value={parentEmail}
                onChange={(e) => setParentEmail(e.target.value)}
                placeholder="parent@example.com"
                className="mt-1 block w-full sm:w-96 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
              />
            </div>

            <div>
              <label htmlFor="field-language" className="block text-sm font-medium text-zinc-700">
                Language
              </label>
              <select
                id="field-language"
                value={language}
                onChange={(e) => setLanguage(e.target.value)}
                className="mt-1 block w-full sm:w-44 rounded-md border border-zinc-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
              >
                <option value="">— (none)</option>
                <option value="en">English</option>
                <option value="es">Español</option>
              </select>
            </div>

            {validationError && (
              <p className="text-xs text-red-700 font-medium">{validationError}</p>
            )}

            <div className="flex items-center justify-end gap-3 pt-2 border-t border-zinc-100">
              <button
                type="button"
                onClick={() => {
                  setParentName(reg.parentName || '');
                  setParentPhone(reg.parentPhone || '');
                  setParentEmail(reg.parentEmail || '');
                  setLanguage(reg.language || '');
                }}
                disabled={!dirty}
                className="px-4 py-2 rounded-md text-sm font-medium text-zinc-700 hover:bg-zinc-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Discard changes
              </button>
              <button
                type="button"
                onClick={openConfirm}
                disabled={!dirty || !!validationError}
                className="px-4 py-2 rounded-md text-sm font-semibold bg-lime-400 text-zinc-900 hover:bg-lime-500 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Save
              </button>
            </div>
          </div>
        </div>

        {/* Audit history */}
        <div className="rounded-2xl border border-zinc-200 bg-white px-5 py-5">
          <h2 className="text-xs font-semibold uppercase tracking-widest text-zinc-500 mb-3">
            Audit history (last 10)
          </h2>
          {audits.length === 0 ? (
            <p className="text-sm text-zinc-500">
              No history yet. The first edit you save will appear at the top.
            </p>
          ) : (
            <ul className="space-y-3 text-sm">
              {audits.map((a) => (
                <li key={a.id} className="border-l-2 border-lime-300 pl-3">
                  <div className="flex items-center gap-2 text-xs text-zinc-500">
                    <CheckCircleIcon className="h-3.5 w-3.5 text-lime-500" />
                    <span>{formatAuditTimestamp(a.createdAt)}</span>
                    <span className="text-zinc-400">·</span>
                    <span className="font-mono">{a.actorEmail || a.actorUid}</span>
                  </div>
                  <p className="mt-1 text-zinc-800">{summarizeAudit(a)}</p>
                  {a.reason && (
                    <p className="mt-1 text-xs text-zinc-500">Reason: "{a.reason}"</p>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>

        {confirmOpen && (
          <ConfirmTierChangeModal
            clubName={reg.parentName || reg.id}
            deltas={deltas}
            inFlight={confirmInFlight}
            errorMessage={confirmError}
            onCancel={() => { if (!confirmInFlight) setConfirmOpen(false); }}
            onConfirm={handleConfirmSave}
          />
        )}
      </div>
    );
  }

  // ── Detail loading / error states ───────────────────────────────────
  if (selectedId && loadingDetail) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button type="button" onClick={closeDetail} className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
          <ArrowLeftIcon className="h-4 w-4" /> Back to search
        </button>
        <div className="space-y-3">
          <div className="h-10 rounded-md bg-zinc-100 animate-pulse" />
          <div className="h-32 rounded-2xl bg-zinc-100 animate-pulse" />
          <div className="h-64 rounded-2xl bg-zinc-100 animate-pulse" />
        </div>
      </div>
    );
  }

  if (selectedId && detailError) {
    return (
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <button type="button" onClick={closeDetail} className="inline-flex items-center gap-1 text-sm text-zinc-600 hover:text-zinc-900 mb-4">
          <ArrowLeftIcon className="h-4 w-4" /> Back to search
        </button>
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-12 text-center text-zinc-600">
          {detailError === 'not-found' ? "This registration doesn't exist." : detailError}
        </div>
      </div>
    );
  }

  // ── Search view ─────────────────────────────────────────────────────
  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
      <h1
        className="text-3xl font-bold uppercase tracking-tight text-zinc-900 mb-1"
        style={{ fontFamily: HEAD_FONT }}
      >
        Registrations
      </h1>
      <p className="text-sm text-zinc-500 mb-5">
        Search by parent phone, parent email, or registration ID.
      </p>

      <form
        onSubmit={(e) => { e.preventDefault(); runSearch(); }}
        className="flex gap-2 mb-4"
      >
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-zinc-400 pointer-events-none" />
          <input
            type="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="+13235551234, parent@example.com, or registrationId"
            className="w-full rounded-md border border-zinc-200 pl-10 pr-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-lime-400"
          />
        </div>
        <button
          type="submit"
          disabled={searching || !searchInput.trim()}
          className="px-4 py-2 rounded-md text-sm font-semibold bg-lime-400 text-zinc-900 hover:bg-lime-500 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {searching ? 'Searching…' : 'Search'}
        </button>
      </form>

      {searchError && (
        <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800 flex items-start gap-2 mb-4">
          <ExclamationTriangleIcon className="h-5 w-5 text-red-500 shrink-0 mt-0.5" />
          <span>{searchError}</span>
        </div>
      )}

      {searching && (
        <div className="space-y-2">
          {[0, 1].map((i) => (
            <div key={i} className="h-14 rounded-md bg-zinc-100 animate-pulse" />
          ))}
        </div>
      )}

      {results && results.length === 0 && !searching && (
        <div className="rounded-2xl border border-zinc-200 bg-white px-6 py-8 text-center text-sm text-zinc-500">
          No registrations matched.
        </div>
      )}

      {results && results.length > 0 && (
        <>
          <p className="text-xs text-zinc-500 mb-2">{results.length} result{results.length === 1 ? '' : 's'}</p>
          <ul className="space-y-2">
            {results.map((r) => (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => openDetail(r.id)}
                  className="w-full text-left rounded-2xl border border-zinc-200 bg-white px-4 py-3 hover:bg-zinc-50"
                >
                  <p className="font-semibold text-zinc-900">
                    {r.parentName || <span className="text-zinc-400 italic">(no name)</span>}
                  </p>
                  <p className="text-xs text-zinc-600 mt-0.5">
                    <span className="font-mono">{r.parentPhone || '—'}</span>
                    {' · '}
                    {r.parentEmail || '—'}
                    {' · '}
                    {r.language || '—'}
                  </p>
                  <p className="text-[10px] text-zinc-400 font-mono mt-0.5">{r.id}</p>
                </button>
              </li>
            ))}
          </ul>
        </>
      )}
    </div>
  );
}

// Audit-feed summary line — covers both setClubTier and
// setRegistrationContact audit shapes since both live in adminAudit/.
function summarizeAudit(a) {
  const before = a.before || {};
  const after  = a.after  || {};
  const fields = a.action === 'setRegistrationContact'
    ? ['parentName', 'parentPhone', 'parentEmail', 'language']
    : ['tier', 'foundingClubNumber', 'trialEndsAt', 'seatLimit'];
  const parts = [];
  for (const f of fields) {
    const b = renderForFeed(before[f]);
    const af = renderForFeed(after[f]);
    if (b !== af) parts.push(`${labelOf(f)}: ${b ?? '—'} → ${af ?? '—'}`);
  }
  if (parts.length === 0) return 'No-op write.';
  return parts.join(' · ');
}

function labelOf(f) {
  switch (f) {
    case 'parentName':  return 'name';
    case 'parentPhone': return 'phone';
    case 'parentEmail': return 'email';
    case 'language':    return 'lang';
    default: return f;
  }
}

function renderForFeed(v) {
  if (v == null) return null;
  if (typeof v === 'object' && typeof v.toDate === 'function') {
    return v.toDate().toISOString().slice(0, 10);
  }
  if (v instanceof Date) return v.toISOString().slice(0, 10);
  return String(v);
}
